import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RowActions } from "@/components/dashboard/RowActions";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Paperclip, ChevronDown, ChevronRight, Sparkles, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import WhatsAppLink from "@/components/dashboard/WhatsAppLink";
import usePagination from "@/lib/usePagination";
import TablePagination from "@/components/dashboard/TablePagination";

const STATUSES = ["submitted", "reviewing", "shortlisted", "interviewed", "offered", "rejected", "hired"];
const STATUS_TONE = {
  submitted: "bg-slateBg text-navy",
  reviewing: "bg-navy text-gold hover:bg-navy",
  shortlisted: "bg-gold text-white hover:bg-gold",
  interviewed: "bg-navy text-gold hover:bg-navy",
  offered: "bg-emerald-600 text-white hover:bg-emerald-600",
  rejected: "bg-red-600 text-white hover:bg-red-600",
  hired: "bg-emerald-700 text-white hover:bg-emerald-700",
};

function AIScoreCell({ row }) {
  const score = row.ai_score;
  const hasScore = typeof score === "number";
  const hasError = !hasScore && row.ai_score_error;
  const pending = !hasScore && !hasError;

  if (pending) {
    return (
      <div className="inline-flex items-center gap-1.5 text-navy/50 text-xs" data-testid={`ai-pending-${row.application_id}`}>
        <Loader2 size={12} className="animate-spin" /> Scoring…
      </div>
    );
  }
  if (hasError) {
    return (
      <span className="text-red-600 text-xs" title={row.ai_score_error} data-testid={`ai-error-${row.application_id}`}>Error</span>
    );
  }
  const tone =
    score >= 80 ? "bg-emerald-600 text-white" :
    score >= 60 ? "bg-navy text-gold" :
    score >= 40 ? "bg-gold/90 text-navy" :
                  "bg-red-500 text-white";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold ${tone}`} data-testid={`ai-score-${row.application_id}`}>
      <Sparkles size={11} /> {score}<span className="opacity-70 font-normal">/100</span>
    </span>
  );
}

function AIDetails({ row }) {
  const strengths = row.ai_strengths || [];
  const gaps = row.ai_gaps || [];
  const summary = row.ai_summary || "";
  const scoredAt = row.ai_scored_at;
  if (!strengths.length && !gaps.length && !summary && !row.ai_score_error) {
    return <div className="text-sm text-navy/50 italic">AI is still analysing this resume. Refresh in a moment.</div>;
  }
  return (
    <div className="grid md:grid-cols-2 gap-6 text-sm" data-testid={`ai-details-${row.application_id}`}>
      {summary && (
        <div className="md:col-span-2">
          <div className="label-eyebrow text-navy/50 mb-1">Summary</div>
          <div className="text-navy/85 leading-snug">{summary}</div>
        </div>
      )}
      <div>
        <div className="label-eyebrow text-emerald-700 mb-2">Strengths</div>
        {strengths.length ? (
          <ul className="space-y-1.5">
            {strengths.map((s, i) => (
              <li key={i} className="flex items-baseline gap-2 text-navy/85">
                <span className="text-emerald-600">◆</span>{s}
              </li>
            ))}
          </ul>
        ) : <div className="text-navy/40 text-xs">—</div>}
      </div>
      <div>
        <div className="label-eyebrow text-red-600 mb-2">Gaps</div>
        {gaps.length ? (
          <ul className="space-y-1.5">
            {gaps.map((g, i) => (
              <li key={i} className="flex items-baseline gap-2 text-navy/85">
                <span className="text-red-500">◆</span>{g}
              </li>
            ))}
          </ul>
        ) : <div className="text-navy/40 text-xs">—</div>}
      </div>
      {row.ai_score_error && (
        <div className="md:col-span-2 text-xs text-red-600 border-l-2 border-red-500 pl-3">
          Scoring error: {row.ai_score_error}
        </div>
      )}
      {scoredAt && (
        <div className="md:col-span-2 text-[10px] uppercase tracking-wider text-navy/40 pt-2 border-t border-navy/10">
          Scored on {new Date(scoredAt).toLocaleString()} · Powered by Ravemora AI
        </div>
      )}
    </div>
  );
}

export default function Applications() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState({});
  const { page, setPage, totalPages, pageItems, rangeStart, rangeEnd, total } = usePagination(rows, 20);
  const load = async () => setRows((await api.get("/applications")).data);
  useEffect(() => { load(); }, []);

  // Auto-poll if any row is still pending AI scoring (max ~90s)
  useEffect(() => {
    const anyPending = rows.some((r) => typeof r.ai_score !== "number" && !r.ai_score_error);
    if (!anyPending) return;
    const t = setInterval(load, 6000);
    const stop = setTimeout(() => clearInterval(t), 90000);
    return () => { clearInterval(t); clearTimeout(stop); };
  }, [rows]);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/applications/${id}`, { status }); toast.success("Status updated"); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    try { await api.delete(`/applications/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const colCount = isAdmin ? 9 : 4;

  return (
    <div className="space-y-8" data-testid="applications-page">
      <div>
        <div className="label-eyebrow text-navy/50">Applications</div>
        <h1 className="serif-display text-4xl mt-2">{isAdmin ? "Received applications" : "My applications"}</h1>
        {isAdmin && (
          <p className="mt-2 text-navy/60 text-sm max-w-2xl">
            Every application is auto-scored against its job description by Ravemora AI. Click the row to expand and see the strengths & gaps.
          </p>
        )}
      </div>
      <div className="bg-white border border-navy/10">
        <Table>
          <TableHeader>
            <TableRow>
              {isAdmin && <TableHead className="w-8"></TableHead>}
              <TableHead>{isAdmin ? "Candidate" : "Applied on"}</TableHead>
              {isAdmin && <TableHead>Email</TableHead>}
              <TableHead>Job</TableHead>
              {isAdmin && <TableHead>AI Score</TableHead>}
              <TableHead>Status</TableHead>
              <TableHead>Resume</TableHead>
              {isAdmin && <TableHead>Submitted</TableHead>}
              {isAdmin && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={colCount} className="text-center text-navy/50 py-8">No applications yet.</TableCell></TableRow>}
            {pageItems.map((r) => (
              <React.Fragment key={r.application_id}>
                <TableRow>
                  {isAdmin && (
                    <TableCell className="w-8 align-middle">
                      <button
                        type="button"
                        onClick={() => toggle(r.application_id)}
                        className="text-navy/50 hover:text-navy"
                        aria-label={expanded[r.application_id] ? "Collapse" : "Expand"}
                        data-testid={`toggle-ai-${r.application_id}`}
                      >
                        {expanded[r.application_id] ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                      </button>
                    </TableCell>
                  )}
                  <TableCell className="serif-display">{isAdmin ? r.full_name : new Date(r.submitted_at).toLocaleString()}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-navy/80 text-sm">{r.email}</span>
                        {r.phone && (
                          <WhatsAppLink
                            phone={r.phone}
                            message={`Hi ${(r.full_name || "").split(" ")[0] || "there"}, this is Ravemora regarding your application.`}
                            testid={`whatsapp-app-${r.application_id}`}
                          />
                        )}
                      </div>
                    </TableCell>
                  )}
                  <TableCell className="text-navy/70 text-sm">{r.job_id}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <AIScoreCell row={r} />
                    </TableCell>
                  )}
                  <TableCell>
                    {isAdmin ? (
                      <Select value={r.status} onValueChange={(v) => updateStatus(r.application_id, v)}>
                        <SelectTrigger className="w-36 h-8" data-testid={`status-${r.application_id}`}><SelectValue/></SelectTrigger>
                        <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Badge className={STATUS_TONE[r.status] || STATUS_TONE.submitted} data-testid={`status-readonly-${r.application_id}`}>{r.status}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.resume_filename ? (
                      <a
                        href={`${API}/applications/${r.application_id}/resume`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-navy/80 hover:text-gold"
                        data-testid={`download-resume-${r.application_id}`}
                      >
                        <Paperclip size={14}/>
                        <span className="max-w-[140px] truncate">{r.resume_filename}</span>
                      </a>
                    ) : (
                      <span className="text-navy/40 text-sm">—</span>
                    )}
                    {r.resume_uploaded_at && (
                      <div className="text-[10px] text-navy/40 mt-0.5">{new Date(r.resume_uploaded_at).toLocaleString()}</div>
                    )}
                  </TableCell>
                  {isAdmin && <TableCell className="text-navy/60 text-sm">{new Date(r.submitted_at).toLocaleString()}</TableCell>}
                  {isAdmin && (
                    <TableCell>
                      <RowActions
                        onDelete={() => del(r.application_id)}
                        deleteTestid={`delete-application-${r.application_id}`}
                        itemLabel="this application"
                      />
                    </TableCell>
                  )}
                </TableRow>
                {isAdmin && expanded[r.application_id] && (
                  <TableRow>
                    <TableCell colSpan={colCount} className="bg-slateBg/40 p-6">
                      <AIDetails row={r} />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          page={page} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          testidPrefix="applications-table"
        />
      </div>
    </div>
  );
}
